import { useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Field } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { EmptyState } from "../ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { WorkbenchPage, WorkbenchPageContent, WorkbenchPageHeader, WorkbenchPageToolbar } from "../ui/workbench-page";
import type { Book } from "../../types";
import type { CitationStyle } from "../../../shared/bookType";
import {
  RESEARCH_ASSET_KIND_LABELS,
  RESEARCH_ASSET_KINDS,
  RESEARCH_BLOB_MAX_BYTES,
  createResearchAsset,
  fetchWebpageResearch,
  getResearchState,
  guessWebpageTitle,
  kindFromMimeType,
  removeResearchAsset,
  upsertResearchAsset,
  withResearchInMetadata,
  type ResearchAsset,
  type ResearchAssetKind,
} from "../../../shared/researchAssets";
import {
  CITATION_ITEM_TYPES,
  createCitation,
  formatBibliographyEntry,
  formatInTextCitation,
  getCitationState,
  removeCitation,
  upsertCitation,
  withCitationsInMetadata,
  type Citation,
  type CslJsonItem,
} from "../../../shared/citations";
import { cn } from "../../lib/utils";

type Props = {
  book: Book;
  onBookChange: (updates: Partial<Book>) => void;
  citationsEnabled: boolean;
  onInsertCitation?: (citation: Citation) => void;
  onInsertImageAsset?: (asset: ResearchAsset) => void;
};

function persistResearch(book: Book, assets: ResearchAsset[], onBookChange: Props["onBookChange"]) {
  const meta = book.metadata as Record<string, unknown>;
  onBookChange({
    metadata: withResearchInMetadata(meta, { assets }) as Book["metadata"],
  });
}

function persistCitations(
  book: Book,
  state: ReturnType<typeof getCitationState>,
  onBookChange: Props["onBookChange"]
) {
  const meta = book.metadata as Record<string, unknown>;
  onBookChange({
    metadata: withCitationsInMetadata(meta, state) as Book["metadata"],
  });
}

async function readFileAsText(file: File): Promise<string> {
  return await file.text();
}

async function readFileAsDataUrl(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function ResearchPanel({
  book,
  onBookChange,
  citationsEnabled,
  onInsertCitation,
  onInsertImageAsset,
}: Props) {
  const research = useMemo(
    () => getResearchState(book.metadata as Record<string, unknown>),
    [book.metadata]
  );
  const citations = useMemo(
    () => getCitationState(book.metadata as Record<string, unknown>),
    [book.metadata]
  );
  const [tab, setTab] = useState<"assets" | "citations">("assets");
  const [kindFilter, setKindFilter] = useState<ResearchAssetKind | "all">("all");
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedCitationId, setSelectedCitationId] = useState<string | null>(null);
  const [webpageUrl, setWebpageUrl] = useState("");
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [webpageBusy, setWebpageBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const filteredAssets = useMemo(() => {
    const list =
      kindFilter === "all" ? research.assets : research.assets.filter((a) => a.kind === kindFilter);
    return [...list].sort((a, b) => a.title.localeCompare(b.title));
  }, [research.assets, kindFilter]);

  const selectedAsset =
    filteredAssets.find((a) => a.id === selectedAssetId) ??
    research.assets.find((a) => a.id === selectedAssetId) ??
    null;
  const selectedCitation =
    citations.items.find((c) => c.id === selectedCitationId) ?? null;

  async function addFiles(files: FileList | null) {
    if (!files?.length) return;
    setLibraryError(null);
    let state = research;
    const rejected: string[] = [];
    for (const file of Array.from(files)) {
      if (file.size > RESEARCH_BLOB_MAX_BYTES) {
        rejected.push(
          `"${file.name}" is larger than ${Math.round(RESEARCH_BLOB_MAX_BYTES / 1_000_000)} MB.`
        );
        continue;
      }
      const kind = kindFromMimeType(file.type, file.name);
      let extractedText: string | undefined;
      let previewDataUrl: string | undefined;
      if (kind === "image") {
        try {
          previewDataUrl = await readFileAsDataUrl(file);
        } catch {
          previewDataUrl = undefined;
        }
      } else if (
        kind === "note" ||
        file.type.startsWith("text/") ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".md")
      ) {
        try {
          extractedText = await readFileAsText(file);
        } catch {
          extractedText = undefined;
        }
      }
      const asset = createResearchAsset({
        kind,
        title: file.name.replace(/\.[^.]+$/, "") || file.name,
        mimeType: file.type || undefined,
        extractedText,
        previewDataUrl,
      });
      state = upsertResearchAsset(state, asset);
      setSelectedAssetId(asset.id);
    }
    persistResearch(book, state.assets, onBookChange);
    if (rejected.length) setLibraryError(rejected.join(" "));
  }

  function addNote() {
    const asset = createResearchAsset({ kind: "note", title: "Research note", extractedText: "" });
    persistResearch(book, upsertResearchAsset(research, asset).assets, onBookChange);
    setSelectedAssetId(asset.id);
  }

  async function addWebpage() {
    const url = webpageUrl.trim();
    if (!url) return;
    setLibraryError(null);
    setWebpageBusy(true);
    try {
      let title = guessWebpageTitle(url);
      let extractedText = "";
      let sourceUrl = url;
      try {
        let fetched: { title: string; extractedText: string; sourceUrl: string } | null = null;
        if (typeof window.desktopV2Api.fetchResearchWebpage === "function") {
          try {
            fetched = await window.desktopV2Api.fetchResearchWebpage(url);
          } catch {
            fetched = null;
          }
        }
        if (!fetched?.title && !fetched?.extractedText) {
          fetched = await fetchWebpageResearch(url);
        }
        title = fetched.title || title;
        extractedText = fetched.extractedText || "";
        sourceUrl = fetched.sourceUrl || url;
      } catch (error) {
        setLibraryError(
          error instanceof Error ? error.message : "Could not fetch that page. Saved the URL instead."
        );
      }
      const asset = createResearchAsset({
        kind: "webpage",
        title,
        sourceUrl,
        extractedText: extractedText || undefined,
        notes: extractedText
          ? "Saved webpage (title and excerpt captured)."
          : "Saved URL. Paste excerpts into notes or extracted text for chat.",
      });
      persistResearch(book, upsertResearchAsset(research, asset).assets, onBookChange);
      setWebpageUrl("");
      setSelectedAssetId(asset.id);
    } finally {
      setWebpageBusy(false);
    }
  }

  function patchAsset(patch: Partial<ResearchAsset>) {
    if (!selectedAsset) return;
    persistResearch(
      book,
      upsertResearchAsset(research, { ...selectedAsset, ...patch }).assets,
      onBookChange
    );
  }

  function addBlankCitation() {
    const citation = createCitation({
      type: "book",
      title: "Untitled source",
      author: [{ family: "Author", given: "" }],
    });
    persistCitations(book, upsertCitation(citations, citation), onBookChange);
    setSelectedCitationId(citation.id);
    setTab("citations");
  }

  function patchCsl(patch: Partial<CslJsonItem>) {
    if (!selectedCitation) return;
    persistCitations(
      book,
      upsertCitation(citations, { ...selectedCitation, csl: { ...selectedCitation.csl, ...patch } }),
      onBookChange
    );
  }

  return (
    <WorkbenchPage>
      <WorkbenchPageHeader
        title="Research"
        description="Sources, files, and notes for this book. The assistant can search this library."
        actions={
          tab === "assets" ? (
            <>
              <Button size="sm" variant="outline" onClick={addNote}>
                Add note
              </Button>
              <Button size="sm" onClick={() => fileRef.current?.click()}>
                Add file
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={addBlankCitation} disabled={!citationsEnabled}>
              Add source
            </Button>
          )
        }
      />
      <input
        ref={fileRef}
        type="file"
        className="sr-only"
        multiple
        onChange={(event) => {
          void addFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <Tabs value={citationsEnabled ? tab : "assets"} onValueChange={(value) => setTab(value as "assets" | "citations")} className="min-h-0 flex-1">
        <WorkbenchPageToolbar>
          <TabsList variant="line">
            <TabsTrigger value="assets">Library</TabsTrigger>
            {citationsEnabled ? <TabsTrigger value="citations">Citations</TabsTrigger> : null}
          </TabsList>
        </WorkbenchPageToolbar>
        <WorkbenchPageContent>
          <TabsContent value="assets" className="mt-0">
            <div className="grid min-h-[28rem] grid-cols-1 gap-4 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
              <div className="flex flex-col gap-2">
                <Select
                  value={kindFilter}
                  onValueChange={(value) => setKindFilter(value as ResearchAssetKind | "all")}
                >
                  <SelectTrigger aria-label="Filter assets by kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All kinds</SelectItem>
                    {RESEARCH_ASSET_KINDS.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {RESEARCH_ASSET_KIND_LABELS[kind]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input
                    value={webpageUrl}
                    onChange={(event) => setWebpageUrl(event.target.value)}
                    placeholder="https://…"
                    aria-label="Webpage URL"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => void addWebpage()} disabled={!webpageUrl.trim() || webpageBusy}>
                    {webpageBusy ? "Saving…" : "Save URL"}
                  </Button>
                </div>
                {libraryError ? (
                  <p className="text-xs text-destructive" role="alert">
                    {libraryError}
                  </p>
                ) : null}
                <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-1.5">
                  {filteredAssets.length === 0 ? (
                    <li className="px-2 py-6 text-center text-xs text-muted-foreground">No assets yet</li>
                  ) : (
                    filteredAssets.map((asset) => (
                      <li key={asset.id}>
                        <button
                          type="button"
                          className={cn(
                            "w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/50",
                            selectedAsset?.id === asset.id && "bg-primary/10 text-foreground"
                          )}
                          onClick={() => setSelectedAssetId(asset.id)}
                        >
                          <span className="block truncate font-medium">{asset.title}</span>
                          <span className="block text-[0.7rem] text-muted-foreground">
                            {RESEARCH_ASSET_KIND_LABELS[asset.kind]}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                {selectedAsset ? (
                  <div className="flex flex-col gap-3">
                    <Field label="Title">
                      <Input
                        value={selectedAsset.title}
                        onChange={(event) => patchAsset({ title: event.target.value })}
                      />
                    </Field>
                    {selectedAsset.kind === "image" && selectedAsset.previewDataUrl ? (
                      <img
                        src={selectedAsset.previewDataUrl}
                        alt={selectedAsset.title}
                        className="max-h-56 rounded-md border border-border object-contain"
                      />
                    ) : null}
                    {selectedAsset.sourceUrl ? (
                      <p className="truncate text-xs text-muted-foreground">
                        <a href={selectedAsset.sourceUrl} className="underline" target="_blank" rel="noreferrer">
                          {selectedAsset.sourceUrl}
                        </a>
                      </p>
                    ) : null}
                    <Field label="Notes">
                      <Textarea
                        value={selectedAsset.notes ?? ""}
                        onChange={(event) => patchAsset({ notes: event.target.value })}
                        rows={3}
                      />
                    </Field>
                    <Field label="Extracted text (searchable in chat)">
                      <Textarea
                        value={selectedAsset.extractedText ?? ""}
                        onChange={(event) => patchAsset({ extractedText: event.target.value })}
                        rows={8}
                      />
                    </Field>
                    <div className="flex flex-wrap gap-2">
                      {selectedAsset.kind === "image" && onInsertImageAsset ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onInsertImageAsset(selectedAsset)}
                        >
                          Insert in manuscript
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          persistResearch(
                            book,
                            removeResearchAsset(research, selectedAsset.id).assets,
                            onBookChange
                          );
                          setSelectedAssetId(null);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    title="Research library"
                    description="Add files, webpage URLs, notes, or save a chat thread as a summary. The assistant can search titles, tags, and extracted text."
                  />
                )}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="citations" className="mt-0">
            {!citationsEnabled ? (
              <EmptyState
                title="Citations are off for this book type"
                description="Switch the book type to instructional, historical, or article in Settings to enable a citation library and generated references page."
              />
            ) : (
              <div className="grid min-h-[28rem] grid-cols-1 gap-4 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
                <div className="flex flex-col gap-2">
                  <Field label="Style">
                    <Select
                      value={citations.style}
                      onValueChange={(value) =>
                        persistCitations(
                          book,
                          { ...citations, style: value as CitationStyle },
                          onBookChange
                        )
                      }
                    >
                      <SelectTrigger aria-label="Citation style">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="apa">APA</SelectItem>
                        <SelectItem value="mla">MLA</SelectItem>
                        <SelectItem value="chicago">Chicago</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-1.5">
                    {citations.items.length === 0 ? (
                      <li className="px-2 py-6 text-center text-xs text-muted-foreground">No sources yet</li>
                    ) : (
                      citations.items.map((citation) => (
                        <li key={citation.id}>
                          <button
                            type="button"
                            className={cn(
                              "w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/50",
                              selectedCitation?.id === citation.id && "bg-primary/10"
                            )}
                            onClick={() => setSelectedCitationId(citation.id)}
                          >
                            <span className="block truncate font-medium">
                              {citation.csl.title || "Untitled source"}
                            </span>
                            <span className="block truncate text-[0.7rem] text-muted-foreground">
                              {formatInTextCitation(citation, citations.style)}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div className="rounded-lg border border-border bg-card p-4">
                  {selectedCitation ? (
                    <div className="flex flex-col gap-3">
                      <Field label="Type">
                        <Select
                          value={String(selectedCitation.csl.type || "book")}
                          onValueChange={(value) => patchCsl({ type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CITATION_ITEM_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Title">
                        <Input
                          value={selectedCitation.csl.title ?? ""}
                          onChange={(event) => patchCsl({ title: event.target.value })}
                        />
                      </Field>
                      <Field label="Author (family name)">
                        <Input
                          value={selectedCitation.csl.author?.[0]?.family ?? ""}
                          onChange={(event) =>
                            patchCsl({
                              author: [
                                {
                                  family: event.target.value,
                                  given: selectedCitation.csl.author?.[0]?.given,
                                },
                              ],
                            })
                          }
                        />
                      </Field>
                      <Field label="Given name">
                        <Input
                          value={selectedCitation.csl.author?.[0]?.given ?? ""}
                          onChange={(event) =>
                            patchCsl({
                              author: [
                                {
                                  family: selectedCitation.csl.author?.[0]?.family,
                                  given: event.target.value,
                                },
                              ],
                            })
                          }
                        />
                      </Field>
                      <Field label="Year">
                        <Input
                          value={selectedCitation.csl.issued?.["date-parts"]?.[0]?.[0] ?? ""}
                          onChange={(event) => {
                            const year = Number(event.target.value);
                            patchCsl({
                              issued: Number.isFinite(year)
                                ? { "date-parts": [[year]] }
                                : { raw: event.target.value },
                            });
                          }}
                        />
                      </Field>
                      <Field label="Publisher / site">
                        <Input
                          value={selectedCitation.csl.publisher ?? ""}
                          onChange={(event) => patchCsl({ publisher: event.target.value })}
                        />
                      </Field>
                      <Field label="Container (journal, anthology)">
                        <Input
                          value={selectedCitation.csl["container-title"] ?? ""}
                          onChange={(event) => patchCsl({ "container-title": event.target.value })}
                        />
                      </Field>
                      <Field label="URL">
                        <Input
                          value={selectedCitation.csl.URL ?? ""}
                          onChange={(event) => patchCsl({ URL: event.target.value })}
                        />
                      </Field>
                      <Field label="DOI">
                        <Input
                          value={selectedCitation.csl.DOI ?? ""}
                          onChange={(event) => patchCsl({ DOI: event.target.value })}
                        />
                      </Field>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        {formatBibliographyEntry(selectedCitation, citations.style)}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {onInsertCitation ? (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => onInsertCitation(selectedCitation)}
                          >
                            Insert in manuscript
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            persistCitations(book, removeCitation(citations, selectedCitation.id), onBookChange);
                            setSelectedCitationId(null);
                          }}
                        >
                          Delete source
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      title="Citation library"
                      description="Add sources here, then insert them into the manuscript. The References / Works Cited page lists only sources that are cited in the text."
                    />
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        </WorkbenchPageContent>
      </Tabs>
    </WorkbenchPage>
  );
}
